import React, { useState, useEffect } from "react";
import { View, TextInput, TouchableOpacity, ScrollView, ActivityIndicator } from "react-native";
import { Controller } from "react-hook-form";
import { Text, Chip } from "@rneui/themed";
import { CloseIcon } from "../../../svgIcons";
import { useDebounce } from "use-debounce";

type Option = { id: string; name: string };

interface SearchSelectMultiProps {
  name: string;
  control: any;
  label: string;
  placeholder: string;
  fetchOptions: (query: string) => Promise<Option[]>;
  backgroundColor: string;
  loading?: boolean;
  showAllByDefault?: boolean;
  height?: number;
  itemType?: string;
}

export const SearchSelectMulti: React.FC<SearchSelectMultiProps> = ({
  name,
  control,
  label,
  placeholder,
  fetchOptions,
  backgroundColor,
  loading,
  showAllByDefault = false,
  height = 200,
  itemType = "item",
}) => {
  const [options, setOptions] = useState<Option[]>([]);
  const [searchText, setSearchText] = useState("");
  const [initialOptionsLoaded, setInitialOptionsLoaded] = useState(false);

  const [debouncedText] = useDebounce(searchText, 300);

  useEffect(() => {
    if (showAllByDefault && !initialOptionsLoaded) {
      const loadAllOptions = async () => {
        const results = await fetchOptions("");
        setOptions(results);
        setInitialOptionsLoaded(true);
      };

      loadAllOptions();
    }
  }, [showAllByDefault, fetchOptions, initialOptionsLoaded]);

  useEffect(() => {
    const fetch = async () => {
      if (debouncedText === "" && showAllByDefault) {
        const results = await fetchOptions("");
        setOptions(results);
      } else if (debouncedText === "" && !showAllByDefault) {
        setOptions([]);
      } else {
        const results = await fetchOptions(debouncedText);
        setOptions(results);
      }
    };

    fetch();
  }, [debouncedText, fetchOptions, showAllByDefault]);

  const handleSearch = (text: string) => {
    setSearchText(text);
  };

  const loadingText = `Loading ${itemType}s...`;
  const noItemsText = `No ${itemType}s available`;
  const noMatchingItemsText = `No matching ${itemType}s found`;

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value = [] } }) => (
        <View style={{ marginBottom: 20 }}>
          {label && <Text style={{ marginBottom: 8, fontWeight: "bold" }}>{label}</Text>}

          <View style={{ flexDirection: "row", flexWrap: "wrap", marginBottom: 8 }}>
            {value.map((selected: Option) => (
              <Chip
                key={selected.id}
                title={selected.name}
                containerStyle={{ margin: 4 }}
                icon={
                  <TouchableOpacity
                    style={{ marginLeft: 5 }}
                    onPress={() => onChange(value.filter((v: Option) => v.id !== selected.id))}>
                    <CloseIcon fill="#fff" props={{ style: { width: 15 } }} />
                  </TouchableOpacity>
                }
                iconPosition="right"
                buttonStyle={{
                  backgroundColor,
                  paddingRight: 8,
                  paddingLeft: 8,
                }}
              />
            ))}
          </View>

          <TextInput
            value={searchText}
            onChangeText={handleSearch}
            placeholder={loading ? loadingText : placeholder}
            style={{
              borderWidth: 1,
              borderColor: "#ccc",
              borderRadius: 8,
              padding: 10,
              marginBottom: 10,
            }}
            editable={!loading}
          />

          <View style={{ height, borderWidth: 1, borderColor: "#E5E7EB", borderRadius: 8, overflow: "hidden" }}>
            {loading ? (
              <View style={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
                <ActivityIndicator color={backgroundColor} size="small" />
                <Text style={{ color: "#6B7280", marginTop: 8 }}>{loadingText}</Text>
              </View>
            ) : options.length > 0 ? (
              <ScrollView nestedScrollEnabled={true} showsVerticalScrollIndicator={true}>
                {options.map((item) => {
                  const isSelected = value.some((v: Option) => v.id === item.id);
                  const matchIndex = searchText && item.name.toLowerCase().indexOf(searchText.toLowerCase());

                  return (
                    <TouchableOpacity
                      key={item.id}
                      onPress={() => {
                        if (!isSelected) {
                          onChange([...value, item]);
                        }
                      }}
                      style={{
                        padding: 10,
                        backgroundColor: isSelected ? "#f5f5f5" : "white",
                        borderBottomWidth: 1,
                        borderBottomColor: "#f0f0f0",
                      }}>
                      <Text style={{ opacity: isSelected ? 0.5 : 1 }}>
                        {searchText && matchIndex >= 0 ? (
                          <>
                            <Text>{item.name.slice(0, matchIndex)}</Text>
                            <Text style={{ fontWeight: "bold", color: backgroundColor }}>
                              {item.name.slice(matchIndex, matchIndex + searchText.length)}
                            </Text>
                            <Text>{item.name.slice(matchIndex + searchText.length)}</Text>
                          </>
                        ) : (
                          item.name
                        )}
                        {isSelected ? " (already selected)" : ""}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </ScrollView>
            ) : (
              <View style={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
                <Text style={{ color: "#6B7280" }}>{searchText.length > 0 ? noMatchingItemsText : noItemsText}</Text>
              </View>
            )}
          </View>
        </View>
      )}
    />
  );
};
